import Quill, { Delta } from 'quill';

const YOUTUBE_HOSTS = /^(?:www\.)?(?:youtube\.com|youtu\.be|m\.youtube\.com)$/i;
const VIMEO_HOSTS = /^(?:www\.)?(?:player\.)?vimeo\.com$/i;
const INSTAGRAM_HOSTS = /^(?:www\.)?instagram\.com$/i;

function tryParseUrl(raw) {
    if (!raw || typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
        return new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    } catch {
        return null;
    }
}

/** Convert a YouTube watch/share/shorts URL into an embeddable iframe src. */
export function toYouTubeEmbedUrl(raw) {
    const url = tryParseUrl(raw);
    if (!url || !YOUTUBE_HOSTS.test(url.hostname)) return null;

    let videoId = null;

    if (url.hostname.includes('youtu.be')) {
        videoId = url.pathname.split('/').filter(Boolean)[0] || null;
    } else if (url.pathname.startsWith('/embed/')) {
        videoId = url.pathname.split('/')[2] || null;
    } else if (url.pathname.startsWith('/shorts/')) {
        videoId = url.pathname.split('/')[2] || null;
    } else if (url.pathname.startsWith('/live/')) {
        videoId = url.pathname.split('/')[2] || null;
    } else {
        videoId = url.searchParams.get('v');
    }

    if (!videoId || !/^[a-zA-Z0-9_-]{6,}$/.test(videoId)) return null;
    return `https://www.youtube.com/embed/${videoId}`;
}

/** Convert a Vimeo page URL into an embeddable iframe src. */
export function toVimeoEmbedUrl(raw) {
    const url = tryParseUrl(raw);
    if (!url || !VIMEO_HOSTS.test(url.hostname)) return null;

    if (url.hostname.startsWith('player.')) {
        const id = url.pathname.split('/').filter(Boolean).pop();
        return id ? `https://player.vimeo.com/video/${id}` : null;
    }

    const match = url.pathname.match(/\/(?:video\/)?(\d+)/);
    if (!match) return null;
    return `https://player.vimeo.com/video/${match[1]}`;
}

export function toVideoEmbedUrl(raw) {
    return toYouTubeEmbedUrl(raw) || toVimeoEmbedUrl(raw);
}

export function isInstagramUrl(raw) {
    const url = tryParseUrl(raw);
    return !!(url && INSTAGRAM_HOSTS.test(url.hostname));
}

export function normalizeInstagramUrl(raw) {
    const url = tryParseUrl(raw);
    if (!url || !INSTAGRAM_HOSTS.test(url.hostname)) return null;
    url.protocol = 'https:';
    return url.toString();
}

/** Extract first http(s) URL when the whole paste is a lone URL. */
function extractLoneUrl(text) {
    const trimmed = (text || '').trim();
    if (!trimmed) return null;
    if (/^https?:\/\/\S+$/i.test(trimmed)) return trimmed;
    if (/^(?:www\.)?(?:youtube\.com|youtu\.be)\S+$/i.test(trimmed)) {
        return `https://${trimmed.replace(/^\/\//, '')}`;
    }
    if (/^(?:www\.)?instagram\.com\/\S+$/i.test(trimmed)) {
        return `https://${trimmed.replace(/^\/\//, '')}`;
    }
    if (/^(?:www\.)?vimeo\.com\/\S+$/i.test(trimmed)) {
        return `https://${trimmed.replace(/^\/\//, '')}`;
    }
    return null;
}

export function matchMediaTextNode(node, delta) {
    const url = extractLoneUrl(node.data || '');
    if (!url) return delta;

    const embed = toVideoEmbedUrl(url);
    if (embed) {
        return new Delta().insert({ video: embed });
    }

    const ig = normalizeInstagramUrl(url);
    if (ig) {
        return new Delta().insert('Watch on Instagram', { link: ig });
    }

    return delta;
}

export function matchIframeNode(node) {
    const src = node.getAttribute('src');
    const embed = toVideoEmbedUrl(src);
    if (embed) {
        return new Delta().insert({ video: embed });
    }
    return new Delta();
}

export const defaultQuillToolbar = [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'video'],
    ['clean'],
];

export const defaultQuillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
    'video',
];

/** Custom video handler so watch/share URLs become proper embeds. */
export function createVideoToolbarHandler(quill) {
    return function videoHandler() {
        const input = window.prompt(
            'Paste a YouTube or Vimeo URL (Instagram: use the Link button — embeds are not supported)'
        );
        if (!input) return;

        const embed = toVideoEmbedUrl(input);
        if (!embed) {
            if (isInstagramUrl(input)) {
                window.alert(
                    'Instagram cannot be embedded reliably. Select some text and use the Link button, or paste the Instagram URL into the editor to insert a clickable “Watch on Instagram” link.'
                );
                return;
            }
            window.alert('Please paste a valid YouTube or Vimeo URL.');
            return;
        }

        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        quill.insertEmbed(range.index, 'video', embed, Quill.sources.USER);
        quill.setSelection(range.index + 1, 0);
    };
}
